/**
 * One client: a back arrow to Clients, the tab row (Dashboards · Reports ·
 * Data · Users · Client Portal · Benchmarks · KPIs, and Settings on the
 * right), and the open tab. Creating or editing a dashboard or report opens
 * the full-screen builder over it.
 */

import { useState, type ReactNode } from 'react';

import { Builder } from '../reports/Builder.tsx';
import { ClientAccess } from '../reports/ClientAccess.tsx';
import { CreateDialog } from '../reports/CreateDialog.tsx';
import { reportsApi, type Client, type Page, type ReportDocument } from '../reports/reports-api.ts';
import { ClientDataTab } from '../custom/ClientDataTab.tsx';
import { ClientDashboards } from './ClientDashboards.tsx';
import { ClientPortalTab, ClientReportsTab, ClientSettingsTab } from './ClientTabs.tsx';
import { KpiPage } from '../kpis/KpiPage.tsx';
import { EmptyFeature } from './EmptyFeature.tsx';
import { go, hrefFor, type ClientTab } from './routes.ts';
import { useClientWorkspace } from './use-client-workspace.ts';

const TAB_LABELS: [ClientTab, string][] = [
  ['dashboards', 'Dashboards'],
  ['reports', 'Reports'],
  ['data', 'Data'],
  ['users', 'Users'],
  ['client-portal', 'Client Portal'],
  ['benchmarks', 'Benchmarks'],
  ['kpis', 'KPIs'],
];

type Open = { document: ReportDocument; pages: Page[] };

export function ClientPage(props: {
  organisationId: string;
  organisationName: string;
  client: Client;
  tab: ClientTab;
  documentId: string | null;
  explore?: string | undefined;
  onClientsChanged: () => void;
}): ReactNode {
  const { organisationId, client, tab } = props;
  const workspace = useClientWorkspace(organisationId, props.organisationName, client.id);
  const [creating, setCreating] = useState<'dashboard' | 'report' | null>(null);
  const [open, setOpen] = useState<Open | null>(null);
  const documents = workspace.documents ?? [];

  if (open && workspace.catalogue) {
    return (
      <Builder
        key={open.document.id}
        organisationId={organisationId}
        catalogue={workspace.catalogue}
        initial={open}
        client={client}
        branding={workspace.branding}
        onClose={() => {
          setOpen(null);
          workspace.reloadDocuments();
        }}
        onOpenDocument={(documentId) =>
          void reportsApi.get(organisationId, documentId).then(setOpen)
        }
      />
    );
  }

  const tabLink = (key: ClientTab) =>
    hrefFor({ area: 'client', clientId: client.id, tab: key, documentId: null });
  const body = (): ReactNode => {
    if (!workspace.catalogue || workspace.documents === null)
      return <p className="ag-loading">Loading…</p>;
    switch (tab) {
      case 'dashboards':
        return (
          <ClientDashboards
            organisationId={organisationId}
            client={client}
            catalogue={workspace.catalogue}
            documents={documents}
            openId={props.documentId}
            explore={props.explore}
            onCreate={() => setCreating('dashboard')}
            onEdit={setOpen}
            onChanged={workspace.reloadDocuments}
          />
        );
      case 'reports':
        return (
          <ClientReportsTab
            organisationId={organisationId}
            clientId={client.id}
            documents={documents}
            onCreate={() => setCreating('report')}
            onOpen={(id) => void reportsApi.get(organisationId, id).then(setOpen)}
            onChanged={workspace.reloadDocuments}
          />
        );
      case 'data':
        return (
          <ClientDataTab
            organisationId={organisationId}
            client={client}
            catalogue={workspace.catalogue}
            onCustomChanged={workspace.reloadCustom}
          />
        );
      case 'users':
        return (
          <div className="ag-tab-body">
            <ClientAccess organisationId={organisationId} client={client} />
          </div>
        );
      case 'client-portal':
        return (
          <ClientPortalTab
            organisationId={organisationId}
            client={client}
            documents={documents}
            branding={workspace.branding}
            onChanged={workspace.reloadDocuments}
          />
        );
      case 'benchmarks':
        return (
          <EmptyFeature
            title="Benchmarks"
            lead={`See how ${client.name} compares with similar businesses: where it ranks, the industry median and the middle 50%.`}
            points={[
              'Pick a metric and filter peers by industry and country',
              'Read the percentile, median and confidence at a glance',
              'Add a benchmark to a dashboard or share it',
            ]}
          />
        );
      case 'kpis':
        return (
          <KpiPage
            organisationId={organisationId}
            clients={[client]}
            catalogue={workspace.catalogue}
            clientId={client.id}
          />
        );
      case 'settings':
        return (
          <ClientSettingsTab
            key={client.id}
            organisationId={organisationId}
            client={client}
            onChanged={props.onClientsChanged}
          />
        );
    }
  };

  return (
    <section className="ag-page ag-client" aria-label={client.name}>
      <header className="ag-client-head">
        <a className="ag-back" href={hrefFor({ area: 'clients' })} aria-label="Back to clients">
          ‹
        </a>
        <strong className="ag-client-name">{client.name}</strong>
        <nav className="ag-tabs" aria-label={`${client.name} sections`}>
          {TAB_LABELS.map(([key, label]) => (
            <a
              key={key}
              href={tabLink(key)}
              className={tab === key ? 'on' : ''}
              aria-current={tab === key ? 'page' : undefined}
            >
              {label}
            </a>
          ))}
        </nav>
        <a
          className={`ag-tab-settings${tab === 'settings' ? ' on' : ''}`}
          href={tabLink('settings')}
        >
          Settings
        </a>
      </header>
      <div className="ag-client-body">{body()}</div>
      {creating && workspace.catalogue && (
        <CreateDialog
          client={client}
          reports={creating === 'report'}
          templates={workspace.templates}
          documents={documents.filter(
            (document) => (creating === 'report') === (document.kind !== 'DASHBOARD'),
          )}
          onCancel={() => setCreating(null)}
          onCreate={async (input) => {
            const created = await reportsApi.create(organisationId, {
              business_unit_id: client.id,
              ...input,
            });
            setCreating(null);
            workspace.reloadDocuments();
            if (created.document.kind === 'DASHBOARD')
              go({
                area: 'client',
                clientId: client.id,
                tab: 'dashboards',
                documentId: created.document.id,
              });
            setOpen(created);
          }}
        />
      )}
    </section>
  );
}
